import React from "react";
import { useLocation, useNavigate } from "react-router-dom";

import type {
  ContentCardData,
  StorySummary,
} from "../../../@types/portalAluno";
import ContentCardList from "../../../Components/ContentCardList/ContentCardList";
import StoryCarousel from "../../../Components/StoryCarousel/StoryCarousel";
import StoryOverlay from "../../../Components/StoryOverlay/StoryOverlay";
import { PORTAL_HOME_SECTION_LIMIT } from "../../../config/portalAluno/constants";
import { CONTENT_SECTIONS } from "../../../config/portalAluno/contentSections";
import HomePageSkeleton from "./HomePageSkeleton";
import useHomeContents from "../../../hooks/PortalAluno/useHomeContents";
import useHomeStories from "../../../hooks/PortalAluno/useHomeStories";
import usePrefetchListingSections from "../../../hooks/PortalAluno/usePrefetchListingSections";
import { takePendingSharedRoute } from "../../../utils/portalAluno/sharedContentLink";
import "./styles.css";

const EMPTY_STORIES: StorySummary[] = [];

type HomeSection = (typeof CONTENT_SECTIONS)[number];

interface HomeSectionView {
  section: HomeSection;
  cards: ContentCardData[];
  openListing: () => void;
  hasMoreThanCarousel: boolean;
}

const getErrorMessage = (error: unknown) =>
  error instanceof Error && error.message && error.message !== "Network Error"
    ? error.message
    : "Não foi possível carregar os conteúdos. Verifique sua conexão e tente novamente.";

const HomePage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [openedStoryIndex, setOpenedStoryIndex] = React.useState<number | null>(
    null
  );

  const { data, error, isError, isLoading, refetch } = useHomeContents();
  const storiesQuery = useHomeStories();

  usePrefetchListingSections(!isLoading);

  React.useEffect(() => {
    const sharedRoute = takePendingSharedRoute();
    if (sharedRoute) navigate(sharedRoute);
  }, [navigate]);

  const stories = React.useMemo(() => {
    const pages = storiesQuery.data?.pages;

    if (!pages) return EMPTY_STORIES;

    const seenIds = new Set<number>();

    return pages.flatMap((page) =>
      page.stories.filter((story) => {
        if (seenIds.has(story.id)) return false;

        seenIds.add(story.id);
        return true;
      })
    );
  }, [storiesQuery.data]);

  const { fetchNextPage: fetchMoreStories } = storiesQuery;
  const requestedStoryId = Number(
    new URLSearchParams(location.search).get("story")
  );
  React.useEffect(() => {
    if (!Number.isSafeInteger(requestedStoryId) || requestedStoryId <= 0) return;

    const storyIndex = stories.findIndex((story) => story.id === requestedStoryId);
    if (storyIndex >= 0) {
      setOpenedStoryIndex(storyIndex);
    } else if (storiesQuery.hasNextPage && !storiesQuery.isFetchingNextPage) {
      fetchMoreStories();
    }
  }, [requestedStoryId, stories, storiesQuery.hasNextPage, storiesQuery.isFetchingNextPage, fetchMoreStories]);

  const handleLoadMoreStories = React.useCallback(() => {
    fetchMoreStories();
  }, [fetchMoreStories]);

  const handleCloseStory = React.useCallback(() => {
    setOpenedStoryIndex(null);
    if (new URLSearchParams(location.search).has("story")) {
      navigate("/app", { replace: true });
    }
  }, [location.search, navigate]);
  const handleRetry = React.useCallback(() => {
    refetch();
  }, [refetch]);

  const sections = data?.sections ?? null;

  const homeSections = React.useMemo<HomeSectionView[]>(() => {
    if (!sections) return [];

    return CONTENT_SECTIONS.reduce<HomeSectionView[]>((list, section) => {
      const sectionCards = sections[section.homeKey] ?? [];

      if (sectionCards.length === 0) return list;

      list.push({
        section,
        cards: sectionCards.slice(0, PORTAL_HOME_SECTION_LIMIT),
        openListing: () => navigate(section.route),
        hasMoreThanCarousel: sectionCards.length >= PORTAL_HOME_SECTION_LIMIT,
      });

      return list;
    }, []);
  }, [navigate, sections]);

  if (isLoading) {
    return <HomePageSkeleton />;
  }

  if (isError && !data) {
    return (
      <section className="content-page portal-home" aria-label="Home">
        <div className="portal-home__feedback" role="alert">
          <h1>Não foi possível abrir a Home</h1>
          <p>{getErrorMessage(error)}</p>
          <button type="button" onClick={handleRetry}>
            Tentar novamente
          </button>
        </div>
      </section>
    );
  }

  return (
    <section className="content-page portal-home" aria-label="Home">
      <div className="portal-home__inner">
        <StoryCarousel
          stories={stories}
          onOpenStory={setOpenedStoryIndex}
          hasMoreStories={Boolean(storiesQuery.hasNextPage)}
          isLoadingMoreStories={storiesQuery.isFetchingNextPage}
          onLoadMoreStories={handleLoadMoreStories}
        />

        {homeSections.length > 0 ? (
          <div className="portal-home__sections">
            {homeSections.map(
              ({ section, cards, openListing, hasMoreThanCarousel }) => (
                <ContentCardList
                  key={section.kind}
                  title={section.title}
                  cards={cards}
                  layout="row"
                  showViewAll
                  viewAllLabel={`Ver todos os conteúdos de ${section.title}`}
                  onViewAll={openListing}
                  seeMoreLabel={`Abrir a listagem completa de ${section.title}`}
                  onSeeMore={hasMoreThanCarousel ? openListing : undefined}
                />
              )
            )}
          </div>
        ) : (
          <div className="portal-home__feedback" role="status">
            <h1>Nenhum conteúdo disponível</h1>
            <p>
              Assim que houver conteúdo para o seu perfil, ele aparecerá aqui.
            </p>
            <button type="button" onClick={handleRetry}>
              Atualizar
            </button>
          </div>
        )}
      </div>

      {openedStoryIndex !== null && (
        <StoryOverlay
          stories={stories}
          initialStoryIndex={openedStoryIndex}
          onClose={handleCloseStory}
        />
      )}
    </section>
  );
};

export default HomePage;
